import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Archive, CalendarClock, Copy, Download, FileText, FolderOpen, FolderPlus, Link2, Mail, Pencil, Plus, Timer, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { DriveExplorer } from "@/components/graphisme/Drive";
import { toast } from "sonner";
import { eur } from "@/components/accounting/docUtils";
import { GELEMENT_STATUS, GPRIORITY, fmtD, fmtDT, gstatus } from "@/components/graphisme/gUtils";
import { ProjectForm } from "@/pages/Graphisme";
import PlanningTab from "@/components/graphisme/PlanningTab";
import RentabilitePanel from "@/components/graphisme/RentabilitePanel";

const Info = ({ label, children }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-neutral-400">{label}</p>
    <p className="text-sm font-medium mt-0.5 whitespace-pre-line">{children || "—"}</p>
  </div>
);

const NOTIFY_TEMPLATES = {
  statut: (name) => `Bonjour,\n\nLe statut de votre projet « ${name} » a évolué. Vous pouvez suivre son avancement en temps réel dans votre espace client DGA.\n\nNous restons à votre disposition pour toute question.\n\nBien cordialement,\nL'équipe DGA`,
  fichiers: (name) => `Bonjour,\n\nDe nouveaux fichiers sont disponibles dans votre espace DGA pour le projet « ${name} ». Vous pouvez les consulter et les télécharger dès maintenant.\n\nNous restons à votre disposition pour toute question.\n\nBien cordialement,\nL'équipe DGA`,
};

const GTABS = [
  ["overview", "Vue d'ensemble"], ["brief", "Brief & documents"], ["devis", "Devis & facturation"],
  ["planning", "Planification"], ["production", "Production & temps"],
  ["livrables", "Livrables & validations"], ["rentabilite", "Rentabilité"],
];

export default function GraphismeProject() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [clients, setClients] = useState([]);
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("overview");
  const [editOpen, setEditOpen] = useState(false);
  const [newElement, setNewElement] = useState("");
  const [newCreation, setNewCreation] = useState("");
  const [newFolder, setNewFolder] = useState("");
  const [folderParent, setFolderParent] = useState("");
  const [creationFolder, setCreationFolder] = useState("");
  const [renamingCrea, setRenamingCrea] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [slotForm, setSlotForm] = useState({ date: "", start: "09:00", end: "12:00" });
  const [timeInfo, setTimeInfo] = useState(null);
  const [activeTimer, setActiveTimer] = useState(null);
  const [manualTime, setManualTime] = useState({ date: "", start: "", end: "" });
  const [complOpen, setComplOpen] = useState(false);
  const [complPrice, setComplPrice] = useState("");
  const [linkQuoteOpen, setLinkQuoteOpen] = useState(false);
  const [linkable, setLinkable] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyKind, setNotifyKind] = useState("statut");
  const [notifyMsg, setNotifyMsg] = useState("");
  const [searchParams] = useSearchParams();
  const planningRef = useRef(null);

  useEffect(() => {
    if (searchParams.get("planifier") === "1") setTab("planning");
  }, [searchParams, p?.id]); // eslint-disable-line

  const load = () => {
    api.get(`/graphisme/projects/${pid}`).then((r) => setP(r.data)).catch(() => setP(false));
    api.get(`/graphisme/projects/${pid}/time`).then((r) => setTimeInfo(r.data)).catch(() => {});
    api.get("/time/active").then((r) => setActiveTimer(r.data)).catch(() => setActiveTimer(null));
  };
  useEffect(() => { load(); }, [pid]); // eslint-disable-line
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data.filter((c) => !c.archived))).catch(() => {});
    api.get("/graphisme/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  if (p === null) return <div className="p-10 text-center"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin mx-auto" /></div>;
  if (p === false) return <div className="p-10 text-center text-neutral-500">Projet introuvable.</div>;

  const st = gstatus(p.status);
  const pr = GPRIORITY[p.priority] || GPRIORITY.normal;
  const pendingEls = (p.elements || []).filter((e) => ["a_fournir", "a_completer"].includes(e.status));

  const infosCard = (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4" data-testid="gp-infos">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-heading font-bold">Informations générales</h3>
        <select value={p.status} disabled={busy} data-testid="gp-status-select"
          onChange={(e) => act(() => api.patch(`/graphisme/projects/${p.id}`, { status: e.target.value }), "Statut mis à jour")}
          className="border border-neutral-200 rounded-lg px-3 py-1.5 text-sm bg-white">
          {(meta?.statuses || []).map((s) => <option key={s} value={s}>{gstatus(s).label}</option>)}
        </select>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Info label="Client">{p.client_name}</Info>
        <Info label="Date de création">{fmtD(p.created_at)}</Info>
        <Info label="Types de création">{(p.types || []).join(", ")}</Info>
        <Info label="Créations prévues">{p.qty_creations}</Info>
        <Info label="Formats">{p.formats}</Info>
        <Info label="Dimensions">{p.dimensions}</Info>
        <Info label="Supports">{p.supports}</Info>
        <Info label="Délai souhaité">{p.delai}</Info>
        <Info label="Temps estimé">{p.estimated_minutes ? `${Math.floor(p.estimated_minutes / 60)}h${String(p.estimated_minutes % 60).padStart(2, "0")}` : "—"}</Info>
        <Info label="Allers-retours inclus">{p.corrections_included} (utilisés : {p.corrections_used})</Info>
        <Info label="Responsable">{p.responsible}</Info>
        <Info label="Objectif">{p.objectif}</Info>
      </div>
      {p.description && <Info label="Description">{p.description}</Info>}
      {p.brief && <Info label="Brief">{p.brief}</Info>}
    </div>
  );

  const driveCard = (
    <div data-testid="gp-drive">
      <h3 className="font-heading font-bold text-sm mb-3">Documents partagés avec le client</h3>
      <DriveExplorer side="agency" clientId={p.client_id} />
    </div>
  );

  const creationsCard = (
    <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="gp-creations">
      <h3 className="font-heading font-bold mb-1">Créations ({(p.creations || []).length} / {p.qty_creations} prévues)</h3>
      <p className="text-xs text-neutral-400 mb-3">Chaque création a ses versions, ses fichiers (travail / aperçu / livraison) et sa validation client indépendante.</p>
      {(() => {
        const folders = p.folders || [];
        const creaCard = (c) => {
          const lv = (c.versions || []).slice(-1)[0];
          return (
            <div key={c.id} onClick={() => renamingCrea !== c.id && navigate(`/graphisme/projet/${p.id}/creation/${c.id}`)} data-testid={`gp-creation-${c.id}`}
              className="group relative border border-neutral-200 rounded-xl p-3.5 text-left hover:border-neutral-400 hover:shadow-sm transition-all cursor-pointer">
              {renamingCrea === c.id ? (
                <Input autoFocus value={renameVal} data-testid={`gp-creation-rename-${c.id}`}
                  onChange={(e) => setRenameVal(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && renameVal.trim()) act(async () => { await api.patch(`/graphisme/projects/${p.id}/creations/${c.id}`, { name: renameVal.trim() }); setRenamingCrea(null); }, "Création renommée");
                    if (e.key === "Escape") setRenamingCrea(null);
                  }}
                  onBlur={() => setRenamingCrea(null)}
                  className="!py-1 text-sm mb-0.5" />
              ) : (
                <p className="text-sm font-semibold truncate pr-12">{c.name}</p>
              )}
              <p className="text-[11px] text-neutral-400 mt-0.5">
                {lv ? `${lv.label} · ${fmtD(lv.at)}` : "Aucune version"} · {(c.annotations || []).length} annotation(s)
              </p>
              <span className={`inline-block mt-2 text-[11px] rounded-full px-2.5 py-0.5 border ${({
                a_faire: "bg-neutral-100 text-neutral-600 border-neutral-200",
                en_cours: "bg-cyan-50 text-cyan-700 border-cyan-200",
                prete: "bg-blue-50 text-blue-700 border-blue-200",
                envoyee_validation: "bg-indigo-50 text-indigo-700 border-indigo-200",
                modifications_demandees: "bg-yellow-50 text-yellow-700 border-yellow-200",
                validee: "bg-green-50 text-green-700 border-green-200",
              })[c.status]}`}>{({
                a_faire: "À faire", en_cours: "En cours", prete: "Prête",
                envoyee_validation: "Envoyée pour validation",
                modifications_demandees: "Modifications demandées", validee: "Validée",
              })[c.status]}</span>
              <div className="absolute top-2 right-2 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button title="Renommer la création" data-testid={`gp-creation-edit-${c.id}`}
                  onClick={(e) => { e.stopPropagation(); setRenamingCrea(c.id); setRenameVal(c.name); }}
                  className="p-1.5 rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button title="Supprimer la création" data-testid={`gp-creation-del-${c.id}`}
                  onClick={(e) => { e.stopPropagation(); if (window.confirm(`Supprimer la création « ${c.name} » ? Cette action est définitive.`)) act(() => api.delete(`/graphisme/projects/${p.id}/creations/${c.id}`), "Création supprimée"); }}
                  className="p-1.5 rounded-md text-neutral-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        };
        const renderFolder = (folder, depth = 0) => (
          <div key={folder.id} className={depth ? "ml-4" : ""} data-testid={`gp-folder-${folder.id}`}>
            <div className="flex items-center justify-between mb-1.5 mt-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 flex items-center gap-1.5">
                <FolderOpen className="w-3.5 h-3.5" /> {folder.name}
              </p>
              <button onClick={() => act(() => api.delete(`/graphisme/projects/${p.id}/folders/${folder.id}`), "Dossier supprimé (créations conservées)")}
                className="p-1 text-neutral-300 hover:text-red-500 transition-colors" data-testid={`gp-folder-del-${folder.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {(p.creations || []).filter((c) => c.folder_id === folder.id).map(creaCard)}
            </div>
            {(folders.filter((f2) => f2.parent_id === folder.id)).map((sub) => renderFolder(sub, depth + 1))}
          </div>
        );
        const ungrouped = (p.creations || []).filter((c) => !c.folder_id || !folders.some((fd) => fd.id === c.folder_id));
        return (
          <>
            {folders.filter((fd) => !fd.parent_id).map((fd) => renderFolder(fd))}
            {ungrouped.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-2.5 mb-3 mt-2">{ungrouped.map(creaCard)}</div>
            )}
          </>
        );
      })()}
      <div className="flex gap-2">
        <Input placeholder="Nom de la création (ex : Flyer A5, Story Instagram…)" value={newCreation}
          onChange={(e) => setNewCreation(e.target.value)} data-testid="gp-creation-new"
          onKeyDown={(e) => e.key === "Enter" && newCreation.trim() && act(async () => { await api.post(`/graphisme/projects/${p.id}/creations`, { name: newCreation.trim(), folder_id: creationFolder || undefined }); setNewCreation(""); }, "Création ajoutée")} />
        {(p.folders || []).length > 0 && (
          <select value={creationFolder} onChange={(e) => setCreationFolder(e.target.value)} data-testid="gp-creation-folder"
            className="border border-neutral-200 rounded-lg px-2 py-1.5 text-xs bg-white max-w-[140px]">
            <option value="">Sans dossier</option>
            {(p.folders || []).map((fd) => <option key={fd.id} value={fd.id}>{fd.parent_id ? "— " : ""}{fd.name}</option>)}
          </select>
        )}
        <Button variant="outline" size="sm" disabled={!newCreation.trim() || busy} data-testid="gp-creation-add"
          onClick={() => act(async () => { await api.post(`/graphisme/projects/${p.id}/creations`, { name: newCreation.trim(), folder_id: creationFolder || undefined }); setNewCreation(""); }, "Création ajoutée")}>
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
      <div className="flex gap-2 mt-2">
        <Input placeholder="Nouveau dossier (ex : Carte A4, Réseaux sociaux…)" value={newFolder}
          onChange={(e) => setNewFolder(e.target.value)} data-testid="gp-folder-new"
          className="!py-1.5 text-xs"
          onKeyDown={(e) => e.key === "Enter" && newFolder.trim() && act(async () => { await api.post(`/graphisme/projects/${p.id}/folders`, { name: newFolder.trim(), parent_id: folderParent || undefined }); setNewFolder(""); }, "Dossier créé")} />
        {(p.folders || []).length > 0 && (
          <select value={folderParent} onChange={(e) => setFolderParent(e.target.value)} data-testid="gp-folder-parent"
            className="border border-neutral-200 rounded-lg px-2 py-1.5 text-xs bg-white max-w-[140px]">
            <option value="">Racine</option>
            {(p.folders || []).map((fd) => <option key={fd.id} value={fd.id}>{fd.name}</option>)}
          </select>
        )}
        <Button variant="outline" size="sm" disabled={!newFolder.trim() || busy} data-testid="gp-folder-add" className="gap-1"
          onClick={() => act(async () => { await api.post(`/graphisme/projects/${p.id}/folders`, { name: newFolder.trim(), parent_id: folderParent || undefined }); setNewFolder(""); }, "Dossier créé")}>
          <FolderPlus className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );

  const elementsCard = (
    <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="gp-elements">
      <h3 className="font-heading font-bold mb-1">Éléments nécessaires au projet</h3>
      <p className="text-xs text-neutral-400 mb-3">Visibles côté client comme « Éléments en attente de votre part ».</p>
      <div className="space-y-1.5">
        {(p.elements || []).map((e) => {
          const es = GELEMENT_STATUS[e.status] || GELEMENT_STATUS.a_fournir;
          return (
            <div key={e.id} className="flex items-center gap-2 text-sm border border-neutral-100 rounded-lg px-3 py-2" data-testid={`gp-element-${e.id}`}>
              <span className="flex-1">{e.label}</span>
              <select value={e.status} disabled={busy} data-testid={`gp-element-status-${e.id}`}
                onChange={(ev) => act(() => api.patch(`/graphisme/projects/${p.id}/elements/${e.id}`, { status: ev.target.value }), null)}
                className={`text-[11px] rounded-full px-2 py-1 border ${es.cls}`}>
                {Object.entries(GELEMENT_STATUS).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
              </select>
              <button onClick={() => act(() => api.delete(`/graphisme/projects/${p.id}/elements/${e.id}`), "Élément retiré")}
                className="text-neutral-300 hover:text-red-500 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          );
        })}
        {(p.elements || []).length === 0 && <p className="text-sm text-neutral-400">Aucun élément demandé pour l'instant.</p>}
      </div>
      <div className="flex gap-2 mt-3">
        <Input placeholder="Ex : Logo HD, Textes, Photos, Charte graphique…" value={newElement}
          onChange={(e) => setNewElement(e.target.value)} data-testid="gp-element-new"
          onKeyDown={(e) => e.key === "Enter" && newElement.trim() && act(async () => { await api.post(`/graphisme/projects/${p.id}/elements`, { label: newElement.trim() }); setNewElement(""); }, "Élément ajouté")} />
        <Button variant="outline" size="sm" disabled={!newElement.trim() || busy} data-testid="gp-element-add"
          onClick={() => act(async () => { await api.post(`/graphisme/projects/${p.id}/elements`, { label: newElement.trim() }); setNewElement(""); }, "Élément ajouté")}>
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );

  const quotesCard = (
    <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="gp-quotes">
      <h3 className="font-heading font-bold mb-3">Devis liés</h3>
      {(p.quotes || []).length === 0 && <p className="text-sm text-neutral-400">Aucun devis. Créez-le depuis ce projet pour pré-remplir client, prestations et prix.</p>}
      <div className="space-y-1.5">
        {(p.quotes || []).map((q) => (
          <button key={q.id} onClick={() => navigate(`/comptabilite?tab=devis&quote=${q.id}`)} data-testid={`gp-quote-${q.id}`}
            className="w-full flex justify-between items-center text-sm bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 rounded-lg px-3 py-2 transition-colors text-left">
            <span>Devis {q.number ? `N° ${q.number}` : "(brouillon)"}</span>
            <span className="font-medium">{eur(q.total_ttc)}</span>
          </button>
        ))}
      </div>
      <Button variant="outline" size="sm" className="w-full mt-2 gap-1.5" data-testid="gp-link-quote"
        onClick={async () => {
          setLinkQuoteOpen(true); setLinkable(null);
          try { const r = await api.get(`/graphisme/projects/${p.id}/linkable-quotes`); setLinkable(r.data); }
          catch { setLinkable([]); }
        }}>
        <Link2 className="w-3.5 h-3.5" /> Joindre un devis existant
      </Button>
      {p.quote_id && (
        <Button variant="outline" size="sm" className="w-full mt-2 gap-1.5" onClick={() => setComplOpen(true)} data-testid="gp-compl-quote">
          <Plus className="w-3.5 h-3.5" /> Devis complémentaire
        </Button>
      )}
    </div>
  );

  const legacyPlanningCard = (
    <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="gp-planning" ref={planningRef}>
      <h3 className="font-heading font-bold mb-2 flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Créneau manuel</h3>
      {timeInfo?.estimated_minutes > 0 && (
        <div className="text-xs mb-3 space-y-1" data-testid="gp-plan-progress">
          <p className="flex justify-between"><span className="text-neutral-400">Temps estimé</span><span>{Math.floor(timeInfo.estimated_minutes / 60)}h{String(timeInfo.estimated_minutes % 60).padStart(2, "0")}</span></p>
          <p className="flex justify-between"><span className="text-neutral-400">Planifié</span><strong>{Math.floor((p.planned_minutes || 0) / 60)}h{String((p.planned_minutes || 0) % 60).padStart(2, "0")}</strong></p>
        </div>
      )}
      {(p.planned_slots || []).length > 0 && (
        <div className="space-y-1.5 mb-3" data-testid="gp-planned-slot">
          {p.planned_slots.map((s) => (
            <div key={s.block_id} className="flex items-center justify-between text-sm bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-1.5" data-testid={`gp-slot-${s.block_id}`}>
              <span className="font-medium">{fmtD(s.date)} — {Math.floor(s.start_min / 60)}h{String(s.start_min % 60).padStart(2, "0")} à {Math.floor(s.end_min / 60)}h{String(s.end_min % 60).padStart(2, "0")}</span>
              <button className="p-1 text-neutral-400 hover:text-red-500 transition-colors" data-testid={`gp-slot-del-${s.block_id}`}
                onClick={() => act(() => api.delete(`/graphisme/projects/${p.id}/planifier/${s.block_id}`), "Créneau retiré")}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-2" data-testid="gp-plan-form">
        {p.status === "acompte_a_regler" && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="gp-acompte-note">Acompte en attente — la planification avant règlement est une exception (tracée dans l'historique).</p>}
        <Input type="date" value={slotForm.date} onChange={(e) => setSlotForm({ ...slotForm, date: e.target.value })} data-testid="gp-slot-date" />
        <div className="grid grid-cols-2 gap-2">
          <Input type="time" value={slotForm.start} onChange={(e) => {
            const start = e.target.value;
            const upd = { ...slotForm, start };
            const rest = timeInfo?.estimated_minutes ? Math.max(0, timeInfo.estimated_minutes - (p.planned_minutes || 0)) : 0;
            if (rest > 0 && start) {
              const [h, m] = start.split(":").map(Number);
              const endMin = h * 60 + m + rest;
              upd.end = `${String(Math.floor(endMin / 60)).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`;
            }
            setSlotForm(upd);
          }} data-testid="gp-slot-start" />
          <Input type="time" value={slotForm.end} onChange={(e) => setSlotForm({ ...slotForm, end: e.target.value })} data-testid="gp-slot-end" />
        </div>
        <Button size="sm" disabled={busy || !slotForm.date} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="gp-planifier-btn"
          onClick={() => act(async () => {
            const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
            const r = await api.post(`/graphisme/projects/${p.id}/planifier`, { date: slotForm.date, start_min: toMin(slotForm.start), end_min: toMin(slotForm.end) });
            if (r.data.overlap_warning) toast.info("Un autre élément existe sur ce créneau — enregistré quand même");
          }, "Créneau bloqué dans l'agenda — client informé")}>
          Planifier le projet
        </Button>
      </div>
    </div>
  );

  const chronoCard = (
    <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="gp-chrono">
      <h3 className="font-heading font-bold mb-2 flex items-center gap-2"><Timer className="w-4 h-4" /> Temps de production</h3>
      {timeInfo && (
        <div className="text-sm space-y-1 mb-2">
          <p className="flex justify-between"><span className="text-neutral-400">Temps passé</span><strong data-testid="gp-time-spent">{Math.floor(timeInfo.spent_minutes / 60)}h{String(timeInfo.spent_minutes % 60).padStart(2, "0")}</strong></p>
          <p className="flex justify-between"><span className="text-neutral-400">Estimé</span><span>{timeInfo.estimated_minutes ? `${Math.floor(timeInfo.estimated_minutes / 60)}h${String(timeInfo.estimated_minutes % 60).padStart(2, "0")}` : "—"}</span></p>
          <p className="flex justify-between"><span className="text-neutral-400">Restant estimé</span><span data-testid="gp-time-remaining">{timeInfo.estimated_minutes ? `${Math.floor(Math.max(0, timeInfo.estimated_minutes - timeInfo.spent_minutes) / 60)}h${String(Math.max(0, timeInfo.estimated_minutes - timeInfo.spent_minutes) % 60).padStart(2, "0")}` : "—"}</span></p>
          {timeInfo.effective_hourly_rate && <p className="flex justify-between"><span className="text-neutral-400">Taux horaire effectif</span><strong className="text-green-700">{eur(timeInfo.effective_hourly_rate)}/h</strong></p>}
        </div>
      )}
      {activeTimer && p.number && (activeTimer.note || "").startsWith(p.number) ? (
        <Button variant="outline" size="sm" className="w-full" disabled={busy} data-testid="gp-chrono-stop"
          onClick={() => act(() => api.post(`/graphisme/projects/${p.id}/chrono/stop`), "Chronomètre arrêté — temps enregistré")}>
          ■ Arrêter le chrono
        </Button>
      ) : (
        <Button variant="outline" size="sm" className="w-full" disabled={busy || !!activeTimer} data-testid="gp-chrono-start"
          title={activeTimer ? "Un chronomètre est déjà actif" : ""}
          onClick={() => act(() => api.post(`/graphisme/projects/${p.id}/chrono/start`), "Chronomètre démarré")}>
          ▶ Démarrer le chrono
        </Button>
      )}
      <div className="mt-4 pt-3 border-t border-neutral-100">
        <p className="text-xs font-semibold text-neutral-500 mb-2">Ajouter du temps manuellement</p>
        <div className="grid grid-cols-3 gap-2">
          <Input type="date" value={manualTime.date} onChange={(e) => setManualTime({ ...manualTime, date: e.target.value })} data-testid="gp-manual-date" className="text-xs" />
          <Input type="time" value={manualTime.start} onChange={(e) => setManualTime({ ...manualTime, start: e.target.value })} data-testid="gp-manual-start" className="text-xs" />
          <Input type="time" value={manualTime.end} onChange={(e) => setManualTime({ ...manualTime, end: e.target.value })} data-testid="gp-manual-end" className="text-xs" />
        </div>
        <Button variant="outline" size="sm" className="w-full mt-2" disabled={busy || !manualTime.date || !manualTime.start || !manualTime.end} data-testid="gp-manual-add"
          onClick={() => act(async () => {
            await api.post("/time/entries", { client_id: p.client_id, date: manualTime.date, start_time: manualTime.start, end_time: manualTime.end, category: "graphisme", note: `${p.number} · ${p.name}`, graphisme_project_id: p.id });
            setManualTime({ date: "", start: "", end: "" });
          }, "Temps ajouté au projet")}>
          Ajouter ce temps
        </Button>
      </div>
    </div>
  );

  const autoInvoiceCard = p.auto_invoice_id && (
    <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm" data-testid="gp-auto-invoice">
      <p className="font-semibold text-green-800 mb-1">Facture prête</p>
      <p className="text-xs text-green-700 mb-2">Préparée automatiquement à la validation complète — vérifiez-la avant envoi.</p>
      <Button size="sm" className="bg-green-700 hover:bg-green-800 text-white w-full" data-testid="gp-open-invoice"
        onClick={() => navigate(`/comptabilite?tab=factures&invoice=${p.auto_invoice_id}`)}>
        Vérifier la facture
      </Button>
    </div>
  );

  const livraisonCard = ["a_facturer", "attente_reglement", "paye", "livre", "termine"].includes(p.status) && (
    <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="gp-livraison">
      <h3 className="font-heading font-bold mb-2 flex items-center gap-2"><Download className="w-4 h-4" /> Livraison</h3>
      <div className="text-xs space-y-1 mb-3">
        <p className="flex justify-between"><span className="text-neutral-400">Paiement</span>
          <span className={["paye", "livre", "termine"].includes(p.status) ? "text-green-700 font-medium" : "text-red-600 font-medium"}>
            {["paye", "livre", "termine"].includes(p.status) ? "✓ Reçu" : "En attente"}
          </span></p>
        <p className="flex justify-between"><span className="text-neutral-400">Fichiers finaux</span>
          <span className={p.fichiers_prets ? "text-green-700 font-medium" : "text-neutral-500"}>
            {p.fichiers_prets ? "✓ Prêts" : "À préparer"}
          </span></p>
      </div>
      <label className="flex items-start gap-2 text-xs text-neutral-600 mb-3 cursor-pointer" data-testid="gp-livraison-cond">
        <input type="checkbox" className="mt-0.5 rounded" checked={!!p.livraison_conditionnelle}
          onChange={(e) => act(() => api.patch(`/graphisme/projects/${p.id}`, { livraison_conditionnelle: e.target.checked }),
            e.target.checked ? "Livraison conditionnelle activée (paiement + fichiers prêts requis)" : "Livraison conditionnelle désactivée")} />
        Débloquer les fichiers uniquement si paiement reçu ET fichiers prêts
      </label>
      <div className="space-y-2">
        {!p.fichiers_prets && (
          <Button variant="outline" size="sm" className="w-full" disabled={busy} data-testid="gp-fichiers-prets"
            onClick={() => act(async () => {
              const r = await api.post(`/graphisme/projects/${p.id}/fichiers-prets`, { ready: true });
              if (r.data.livre) toast.success("Fichiers prêts + paiement reçu → livraison automatique débloquée");
            }, "Fichiers finaux marqués prêts")}>
            Marquer les fichiers finaux prêts
          </Button>
        )}
        {p.status === "paye" && (
          <Button size="sm" className="w-full bg-neutral-900 hover:bg-neutral-800 text-white" disabled={busy} data-testid="gp-livrer"
            onClick={() => act(() => api.post(`/graphisme/projects/${p.id}/livrer`), "Projet livré — le client peut télécharger ses fichiers")}>
            Livrer au client
          </Button>
        )}
        {p.status === "livre" && <p className="text-xs text-teal-700 bg-teal-50 border border-teal-200 rounded-lg p-2" data-testid="gp-livre-badge">✓ Livré au client</p>}
      </div>
      <div className="mt-3 pt-3 border-t border-neutral-100 space-y-2">
        <label className="flex items-start gap-2 text-xs text-neutral-600 cursor-pointer" data-testid="gp-relance-auto">
          <input type="checkbox" className="mt-0.5 rounded" checked={!!p.relance_auto?.enabled}
            onChange={(e) => act(() => api.patch(`/graphisme/projects/${p.id}`, {
              relance_auto: { enabled: e.target.checked, days: p.relance_auto?.days || 4 } }),
              e.target.checked ? "Relance automatique activée (tous les 4 jours)" : "Relance automatique désactivée")} />
          Relance automatique si pas de réponse (4 jours)
        </label>
        <Button variant="outline" size="sm" className="w-full" disabled={busy} data-testid="gp-relancer"
          onClick={() => act(() => api.post(`/graphisme/projects/${p.id}/relancer`, {}), "Relance envoyée au client")}>
          Relancer le client maintenant
        </Button>
        {p.last_relance_at && <p className="text-[11px] text-neutral-400">Dernière relance : {fmtD(p.last_relance_at)}</p>}
      </div>
    </div>
  );

  const pendingCard = pendingEls.length > 0 && (
    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm" data-testid="gp-pending-elements">
      <p className="font-semibold text-yellow-800 mb-1">Éléments clients en attente ({pendingEls.length})</p>
      <ul className="list-disc list-inside text-yellow-700 text-xs space-y-0.5">
        {pendingEls.map((e) => <li key={e.id}>{e.label}</li>)}
      </ul>
    </div>
  );

  const activityCard = (
    <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="gp-activity">
      <h3 className="font-heading font-bold mb-3">Historique</h3>
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {[...(p.activity || [])].reverse().map((a) => (
          <div key={a.id} className="text-sm border-l-2 border-neutral-200 pl-3">
            <p className="text-neutral-700">{a.text}</p>
            <p className="text-[11px] text-neutral-400">{fmtDT(a.at)}{a.by ? ` · ${a.by}` : ""}</p>
          </div>
        ))}
        {(p.activity || []).length === 0 && <p className="text-sm text-neutral-400">Aucune activité.</p>}
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="gproject-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <button onClick={() => navigate("/graphisme")} className="text-xs text-neutral-400 hover:text-neutral-900 transition-colors">← Graphisme</button>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold mt-1 flex items-center gap-3 flex-wrap">
            {p.name}
            <span className={`text-[11px] rounded-full px-2.5 py-1 border font-normal ${st.cls}`} data-testid="gp-status">{st.label}</span>
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            <span className="font-mono text-xs">{p.number}</span> · {p.client_name} ·
            <span className={`inline-flex items-center gap-1.5 ml-1`}><span className={`w-2 h-2 rounded-full ${pr.dot}`} />{pr.label}</span>
            {p.deadline && <> · Deadline <strong>{fmtD(p.deadline)}</strong></>}
            {p.price_ht > 0 && <> · <strong>{eur(p.price_ht)}</strong> HT</>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!p.quote_id && (
            <Button size="sm" className="bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5" disabled={busy} data-testid="gp-create-quote"
              onClick={() => act(async () => {
                const r = await api.post(`/graphisme/projects/${p.id}/quote`, {});
                toast.success(`Devis créé (brouillon) — retrouvez-le dans Comptabilité`);
                navigate(`/comptabilite?tab=devis&quote=${r.data.id}`);
              }, null)}>
              <FileText className="w-3.5 h-3.5" /> Créer le devis
            </Button>
          )}
          <Button variant="outline" size="sm" className="gap-1.5" data-testid="gp-notify"
            onClick={() => { setNotifyKind("statut"); setNotifyMsg(NOTIFY_TEMPLATES.statut(p.name)); setNotifyOpen(true); }}>
            <Mail className="w-3.5 h-3.5" /> Notifier le client
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setEditOpen(true)} data-testid="gp-edit">
            <Pencil className="w-3.5 h-3.5" /> Modifier
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" disabled={busy} data-testid="gp-duplicate"
            onClick={() => act(async () => { const r = await api.post(`/graphisme/projects/${p.id}/duplicate`); navigate(`/graphisme/projet/${r.data.id}`); }, "Projet dupliqué")}>
            <Copy className="w-3.5 h-3.5" /> Dupliquer
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" disabled={busy} data-testid="gp-archive"
            onClick={() => act(() => api.post(`/graphisme/projects/${p.id}/archive`, { archived: !p.archived }), p.archived ? "Projet restauré" : "Projet archivé")}>
            <Archive className="w-3.5 h-3.5" /> {p.archived ? "Restaurer" : "Archiver"}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-5 border-b border-neutral-100 overflow-x-auto" data-testid="gp-tabs">
        {GTABS.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`gp-tab-${id}`}
            className={`pb-2 text-xs font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === id ? "border-[#0B132B] text-[#0B132B]" : "border-transparent text-neutral-400 hover:text-neutral-700"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">{infosCard}</div>
          <div className="space-y-5">{pendingCard}{activityCard}</div>
        </div>
      )}
      {tab === "brief" && (
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">{driveCard}</div>
          <div className="space-y-5">{elementsCard}</div>
        </div>
      )}
      {tab === "devis" && (
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">{quotesCard}</div>
          <div className="space-y-5">{autoInvoiceCard}</div>
        </div>
      )}
      {tab === "planning" && (
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5"><PlanningTab project={p} onChanged={load} act={act} busy={busy} /></div>
          <div className="space-y-5">{legacyPlanningCard}</div>
        </div>
      )}
      {tab === "production" && (
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">{chronoCard}</div>
          <div className="space-y-5">{pendingCard}</div>
        </div>
      )}
      {tab === "livrables" && (
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">{creationsCard}</div>
          <div className="space-y-5">{livraisonCard}</div>
        </div>
      )}
      {tab === "rentabilite" && (
        <div className="max-w-3xl"><RentabilitePanel project={p} onChanged={load} /></div>
      )}

      <ProjectForm open={editOpen} onClose={() => setEditOpen(false)} onSaved={load} clients={clients} meta={meta} project={p} />

      {complOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setComplOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm space-y-3" onClick={(e) => e.stopPropagation()} data-testid="gp-compl-modal">
            <h3 className="font-heading font-bold">Devis complémentaire</h3>
            <p className="text-xs text-neutral-500">Série de modifications supplémentaires — le client devra l'accepter (ajoute +1 aller-retour au quota une fois validé).</p>
            <Input type="number" min="0" placeholder="Montant HT (€)" value={complPrice} onChange={(e) => setComplPrice(e.target.value)} data-testid="gp-compl-price" />
            <Button disabled={busy || !(+complPrice > 0)} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="gp-compl-save"
              onClick={() => act(async () => {
                await api.post(`/graphisme/projects/${p.id}/quote`, {
                  items: [{ prestation: `Devis complémentaire — modifications supplémentaires (${p.number})`, description: "", qty: 1, price_ht: +complPrice }],
                  comment: `Devis complémentaire au devis initial — projet ${p.number}.`,
                  corrections_added: 1,
                });
                setComplOpen(false); setComplPrice("");
              }, "Devis complémentaire créé (brouillon) — retrouvez-le dans Comptabilité")}>
              Créer le devis complémentaire
            </Button>
          </div>
        </div>
      )}

      {linkQuoteOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setLinkQuoteOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()} data-testid="gp-link-quote-modal">
            <h3 className="font-heading font-bold">Joindre un devis existant</h3>
            <p className="text-xs text-neutral-500">Devis de {p.client_name} non liés à un autre projet. Le devis choisi sera rattaché à ce projet (statut, planning et facturation synchronisés).</p>
            {linkable === null && <p className="text-xs text-neutral-400 animate-pulse">Chargement…</p>}
            {linkable !== null && linkable.length === 0 && (
              <p className="text-xs text-neutral-400" data-testid="gp-linkable-empty">Aucun devis rattachable pour ce client — créez-le depuis l'onglet Devis & facturation.</p>
            )}
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {(linkable || []).map((q) => (
                <button key={q.id} disabled={busy} data-testid={`gp-link-quote-${q.id}`}
                  onClick={() => act(async () => {
                    await api.post(`/graphisme/projects/${p.id}/link-quote`, { quote_id: q.id });
                    setLinkQuoteOpen(false);
                  }, `Devis ${q.number ? `n°${q.number} ` : ""}joint au projet`)}
                  className="w-full flex items-center justify-between text-sm border border-neutral-200 rounded-lg px-3 py-2.5 hover:border-[#1D4ED8]/50 hover:bg-neutral-50 transition-colors text-left">
                  <span>
                    <strong>Devis {q.number ? `n° ${q.number}` : "(brouillon)"}</strong>
                    <span className="text-[11px] text-neutral-400 ml-2">{fmtD(q.created_at)}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className={`text-[10px] rounded-full px-2 py-0.5 border ${q.status === "accepte" ? "bg-green-50 text-green-700 border-green-200" : "bg-neutral-50 text-neutral-500 border-neutral-200"}`}>{q.status}</span>
                    <strong>{eur(q.total_ttc)}</strong>
                  </span>
                </button>
              ))}
            </div>
            <Button variant="outline" className="w-full" onClick={() => setLinkQuoteOpen(false)} data-testid="gp-link-quote-cancel">Annuler</Button>
          </div>
        </div>
      )}

      {notifyOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setNotifyOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()} data-testid="gp-notify-modal">
            <h3 className="font-heading font-bold">Notifier le client</h3>
            <div>
              <label className="text-xs text-neutral-500">Type de notification</label>
              <select className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white mt-1" value={notifyKind}
                onChange={(e) => { setNotifyKind(e.target.value); setNotifyMsg(NOTIFY_TEMPLATES[e.target.value](p.name)); }}
                data-testid="gp-notify-kind">
                <option value="statut">Le statut de votre projet a évolué</option>
                <option value="fichiers">De nouveaux fichiers sont disponibles dans votre espace DGA</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-neutral-500">Corps de l'email (modifiable)</label>
              <Textarea rows={7} className="mt-1" value={notifyMsg} onChange={(e) => setNotifyMsg(e.target.value)} data-testid="gp-notify-message" />
              <p className="text-[11px] text-neutral-400 mt-1">Un bouton « Accéder aux projets » renvoyant vers la page de connexion client sera ajouté automatiquement à l'email.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setNotifyOpen(false)} data-testid="gp-notify-cancel">Annuler</Button>
              <Button className="flex-1 bg-neutral-900 hover:bg-neutral-800 text-white" disabled={busy || !notifyMsg.trim()} data-testid="gp-notify-send"
                onClick={() => act(async () => {
                  const r = await api.post(`/graphisme/projects/${p.id}/notify-client`, { kind: notifyKind, message: notifyMsg.trim() });
                  toast.success(`Notification envoyée à ${r.data.email}`);
                  setNotifyOpen(false);
                }, null)}>
                Envoyer la notification
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
